<template>
  <span style="display: contents">
    <v-tooltip
      v-for="(day, index) in workScheduleBlocks"
      v-bind:key="index"
      bottom
    >
      <template v-slot:activator="{ on }">
        <v-card
          v-on="on"
          tile
          :style="workScheduleStyle(day)"
          :class="{
            'workScheduleCard': isWeekView && !isMobile(),
            'workScheduleEarlyDivider': doesShiftStartInMorning(day) && !isRegularShift(day),
            'workScheduleLateDivider': !doesShiftStartInMorning(day) && !isRegularShift(day),
          }"
          @click="$emit('viewMoreInfo', day)"
        >
          <v-row
            class="ma-0 mt-3"
            justify="start"
            style="overflow-x: visible"
          >
            <div class="text-center text-truncate mx-2">
              {{
                day.type === 'ABSENCE'
                        ? day.externalCode
                        : getWorkScheduleExternalNameBySchedule(day)
              }}
            </div>
          </v-row>
          <v-row
            v-show="shouldDisplayTimeInformation && day.type !== 'ABSENCE'"
            class="ma-0 mt-3 fill-height"
            justify="start"
            style="font-size: 14px"
          >
            <div class="text-center text-truncate mx-2">
              {{
              day.isOffDay ? $t('misc.nonWorkingDay') : day.externalCode === null || day.type !== "WORK_SCHEDULE" ?
                '' : getWorkScheduleTimeStartAndEnd(day)
              }}
            </div>
          </v-row>
        </v-card>
      </template>
      {{ day.externalCode ? day.externalCode : $t('misc.none') }} <br>
      {{
        day.isOffDay ? $t('misc.nonWorkingDay') : day.externalCode === null || day.type !== "WORK_SCHEDULE" ?
          '' : getWorkScheduleTimeStartAndEnd(day)
      }}
    </v-tooltip>
  </span>
</template>

<script>
import getWorkScheduleExternalNameBySchedule from '@/mixins/getWorkScheduleExternalName';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import addMinutes from 'date-fns/addMinutes';
import displayAdaptable from '@/mixins/displayAdaptable';
import isNil from 'lodash/isNil';

export default {
  name: 'WorkScheduleBlock',

  mixins: [
    getWorkScheduleExternalNameBySchedule,
    displayAdaptable,
  ],

  props: {
    isWeekView: Boolean,
    viewRange: {},
    schedule: {},
  },

  data() {
    return {
      shouldDisplayTimeInformation: false,
      loading: true,
    };
  },

  computed: {
    workScheduleBlocks() {
      if (this.isWeekView && !this.isMobile()) {
        return this.schedule;
      }

      const blocks = [];
      if (this.schedule) {
        this.schedule.forEach((day) => {
          if (blocks.length === 0 || (blocks.length > 0
            && (blocks[blocks.length - 1].externalCode !== day.externalCode
              || blocks[blocks.length - 1].isOffDay !== day.isOffDay
              || blocks[blocks.length - 1].startTime !== day.startTime
              || blocks[blocks.length - 1].endTime !== day.endTime))) {
            blocks.push({ ...day });
          } else {
            blocks[blocks.length - 1].minuteRange += day.minuteRange;
          }
        });
      }
      return blocks;
    },
  },

  methods: {
    doesShiftStartInMorning(block) {
      return block.startTime
        ? parseInt(block.startTime.slice(0, 2), 10) < 12
        : true;
    },

    isRegularShift(block) {
      const isLateEndShift = block.endTime
        ? parseInt(block.endTime.slice(0, 2), 10) > 16
        : true;
      return block.endTime && block.startTime
        && this.doesShiftStartInMorning(block)
        && isLateEndShift;
    },

    getWorkScheduleRangeWidthAndMargin(block) {
      const viewRangeTotalMinutes = differenceInMinutes(
        addMinutes(this.viewRange.endDate, 1),
        this.viewRange.startDate,
      );

      const blockWidth = block.minuteRange / viewRangeTotalMinutes;

      const blockPercentage = (
        this.isWeekView
        && block.startTime
        && block.endTime
        && !this.isMobile()
        && !block.isOffDay
      ) ? 75
        : 100;

      let margin = blockWidth * (100 - blockPercentage);
      if (this.isRegularShift(block)) {
        margin /= 2;
      }
      const actualWidth = blockWidth * blockPercentage;

      if (Math.floor(actualWidth) > 6) {
        this.shouldDisplayTimeInformation = true;
      }

      return {
        width: actualWidth,
        marginRight: ((this.isRegularShift(block) || this.doesShiftStartInMorning(block)) && this.isWeekView
          && !block.isOffDay) ? margin : 0,
        marginLeft: ((this.isRegularShift(block) || !this.doesShiftStartInMorning(block)) && this.isWeekView
          && !block.isOffDay) ? margin : 0,
      };
    },

    getWorkScheduleTimeStartAndEnd(block) {
      if (!block.startTime || !block.endTime) {
        return '';
      }
      return `${block.startTime} - ${block.endTime}`;
    },

    workScheduleStyle(block) {
      let workScheduleColour;
      if (isNil(block.externalCode)) {
        workScheduleColour = {
          background: 'var(--v-info-darken1)',
          border: 'var(--v-info-darken2)',
          text: 'black',
        };
      } else {
        const fullDetails = this.getWorkScheduleByExternalCode(block.externalCode);
        workScheduleColour = (block.type === 'WORK_SCHEDULE' && fullDetails)
          ? this.getWorkScheduleByExternalCode(block.externalCode).colour
          : {
            background: 'orange',
            border: 'orange',
            text: 'black',
          };
      }

      const widthAndMargin = this.getWorkScheduleRangeWidthAndMargin(block);
      const offDayColour = this.getNonWorkingDayBackground(workScheduleColour.background);

      return {
        height: '100%',
        width: `${widthAndMargin.width}%`,
        backgroundColor: block.isOffDay ? offDayColour : `${workScheduleColour.background}`,
        color: `${workScheduleColour.text}`,
        border: `2px solid ${workScheduleColour.border}`,
        marginRight: `${widthAndMargin.marginRight}%`,
        marginLeft: `${widthAndMargin.marginLeft}%`,
      };
    },

    getNonWorkingDayBackground(colour) {
      return this.lighten(colour, 0.2);
    },

    lighten(colour, luminosity) {
      // validate hex string
      colour = colour.replace(/[^0-9a-f]/gi, '');
      if (colour.length < 6) {
        colour = colour[0] + colour[0] + colour[1] + colour[1] + colour[2] + colour[2];
      }
      luminosity = luminosity || 0;

      // convert to decimal and change luminosity
      let newColor = '#';
      let c;
      let i;
      const black = 0;
      const white = 255;
      for (i = 0; i < 3; i++) {
        c = parseInt(colour.substr(i * 2, 2), 16);
        c = Math.round(Math.min(Math.max(black, c + (luminosity * white)), white)).toString(16);
        newColor += (`00${c}`).substr(c.length);
      }
      return newColor;
    },
  },
};
</script>

<style scoped lang="scss">
@import 'src/scss/variables';

.workScheduleCard:hover {
  -webkit-box-shadow: $boxShadowHover !important;
  box-shadow: $boxShadowHover !important;
  cursor: pointer;
}

.workScheduleCard {
  z-index: 2;
}

.workScheduleCard::after {
  content: '';
  position: absolute;
  top: -30px;
  right: -20%;
  bottom: -30px;
  border-right: 2px solid #E0E0E0;
}

.workScheduleEarlyDivider::after {
  content: '';
  position: absolute;
  right: -37%;
  border-right: 2px solid #E0E0E0;
}

.workScheduleLateDivider::after,
.moreThanOneBlock:last-child::after {
  border-right: 0 solid #E0E0E0;
}
</style>
